import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import PriceEstimator from "@/components/PriceEstimator";
import { CheckCircle2, ArrowRight } from "lucide-react";

export default function ComparadorPage() {
  return (
    <div data-testid="page-comparador">
      <SEO
        title="Comparador de preços de casa no Porto e Grande Porto"
        description="Calcule em segundos quanto vale um apartamento no Porto, Gondomar, Maia, Ermesinde ou Valongo. Estimativa por zona e tipologia com €/m² actual do mercado."
        canonical="https://casanoporto.com/comparador-precos-porto"
      />

      <section className="container-cnp pt-14 lg:pt-20 pb-8">
        <div className="max-w-3xl">
          <span className="tag-chip">Comparador de preços</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.05] tracking-[-0.02em]">
            Quanto vale uma casa<br />
            <span className="italic text-[#103C4A]">no Porto ou Grande Porto?</span>
          </h1>
          <p className="mt-6 text-lg text-stone-600 leading-relaxed">
            Uma estimativa rápida com base no <strong className="text-stone-900 font-medium">€/m² actual</strong> do
            mercado — cruzado com a sua tipologia e zona. Não substitui uma avaliação humana, mas dá-lhe uma
            referência séria em menos de 30 segundos.
          </p>
        </div>
      </section>

      <section className="container-cnp pb-16">
        <PriceEstimator />
      </section>

      <section className="container-cnp py-14 grid lg:grid-cols-12 gap-10 border-t border-stone-200">
        <div className="lg:col-span-7">
          <span className="tag-chip">Como funciona</span>
          <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900 tracking-[-0.02em]">
            Dados reais do mercado, actualizados regularmente.
          </h2>
          <div className="mt-6 space-y-4 text-stone-700 leading-relaxed">
            <p>
              Cruzamos o <strong className="font-medium text-stone-900">€/m² médio</strong> da sua zona (Porto,
              Gondomar, Maia, Ermesinde ou Valongo) com o comportamento típico do mercado português por tipologia —
              apartamentos menores (T0/T1) valem mais por m² que apartamentos maiores (T4+).
            </p>
            <p>
              A estimativa devolve um <strong className="font-medium text-stone-900">valor central</strong> mais um
              intervalo (mín. / máx.) que reflecte a dispersão natural do mercado: estado do imóvel, andar, exposição,
              certificação energética e negociação final.
            </p>
            <p>
              Se indicar o preço a que pensa comprar ou vender, o comparador diz-lhe imediatamente se está{" "}
              <strong className="font-medium text-stone-900">alinhado com o mercado</strong>, abaixo ou acima.
            </p>
          </div>
          <ul className="mt-8 space-y-3 text-stone-700 text-sm">
            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-1 text-[#103C4A]" /> Fontes: Idealista, Ranking Imobiliário e observatórios públicos.</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-1 text-[#103C4A]" /> Actualização trimestral pelos consultores.</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-1 text-[#103C4A]" /> Sem registo e sem partilha do seu contacto.</li>
          </ul>
        </div>
        <aside className="lg:col-span-5">
          <div className="card-soft p-8 sticky top-24">
            <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500">Passo seguinte</div>
            <h3 className="mt-3 font-display text-2xl text-stone-900 tracking-[-0.02em]">
              Quer uma avaliação exacta?
            </h3>
            <p className="mt-3 text-sm text-stone-600">
              Um relatório humano em 48h — comparativos recentes, plano de valorização, estratégia de venda e
              intervalo de preço realista para o seu imóvel específico.
            </p>
            <Link to="/avaliacao-gratuita" data-testid="comparador-cta-avaliacao" className="btn-primary mt-6 w-full justify-center">
              Avaliação gratuita <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contacto" data-testid="comparador-cta-conversa" className="btn-secondary mt-3 w-full justify-center">
              Falar primeiro com a Zélia
            </Link>
          </div>
        </aside>
      </section>
    </div>
  );
}
